/**
 * Save Orchestrator for Two-Way Bound Fields (PRD.150).
 *
 * Inspects a form schema and submitted values, selects eligible `two_way`
 * fields, resolves each through the system field registry, and produces
 * structured domain write operations grouped by target table.
 *
 * Architecture:
 *   - Renderer stays persistence-agnostic — it calls the orchestrator.
 *   - Orchestrator translates form values into domain column maps.
 *   - Caller (page component) executes the operations via db.execute().
 *
 * This module is intentionally pure — no database dependency. It returns
 * data structures the caller can execute.
 */

import type { FormFieldsSchemaV1, FormFieldBindingSource, DomainGroup } from './forms';
import { flattenFields, getEffectiveDomainGroup } from './forms';
import { getSystemFieldByKey, type SystemFieldEntry } from './form-field-registry';

// ─── Types ───

export interface TwoWaySaveCandidate {
  /** Form field key (e.g. 'my_name_field') */
  fieldKey: string;
  /** Full binding key (e.g. 'patient.full_name') */
  bindingKey: string;
  /** Binding source */
  source: FormFieldBindingSource;
  /** Registry entry for this field */
  registryEntry: SystemFieldEntry;
  /** Effective domain group */
  domainGroup: DomainGroup;
  /** The value to write */
  value: string;
}

export interface SkippedField {
  fieldKey: string;
  reason: string;
}

export interface CandidateExtractionResult {
  candidates: TwoWaySaveCandidate[];
  skipped: SkippedField[];
}

/**
 * A structured write operation for a single domain table.
 * The caller builds SQL from this and executes via db.execute().
 */
export interface DomainWriteOp {
  /** Target table name */
  table: string;
  /** Column → value pairs to SET */
  columns: Record<string, string>;
  /** Description of what this operation does (for logging/debugging) */
  description: string;
}

/**
 * A multi-row write operation. Each value in `rows` becomes a separate INSERT,
 * and existing rows matching `filterColumn` are soft-deleted first.
 */
export interface MultiRowWriteOp {
  /** Target table name */
  table: string;
  /** Column that holds the value (e.g. 'substance') */
  valueColumn: string;
  /** Static columns set on every inserted row (e.g. allergy_type = 'drug') */
  staticColumns: Record<string, string>;
  /** Column used to filter existing rows for soft-delete (e.g. 'allergy_type') */
  filterColumn: string;
  /** Filter value (e.g. 'drug') */
  filterValue: string;
  /** The values to insert as individual rows */
  values: string[];
  /** Description */
  description: string;
}

export interface OrchestratorResult {
  /** Single-row write operations (UPDATE/INSERT), grouped by table */
  operations: DomainWriteOp[];
  /** Multi-row write operations (soft-delete + INSERT per value) */
  multiRowOps: MultiRowWriteOp[];
  /** Fields that were successfully mapped to operations */
  saved: TwoWaySaveCandidate[];
  /** Fields that were skipped with reasons */
  skipped: SkippedField[];
}

// ─── Binding Key → Table/Column Mapping ───

/**
 * Maps a registry bindingKey to a target { table, column }.
 * This is the persistence routing layer — the registry defines meaning,
 * this mapping defines where values are physically stored.
 */
const WRITE_TARGET_MAP: Record<string, { table: string; column: string }> = {
  // ── patients table (simple — direct column on patients row) ──
  'patient.full_name': { table: 'patients', column: 'full_name' },
  'patient.birth_date': { table: 'patients', column: 'birth_date' },
  'patient.sex': { table: 'patients', column: 'sex' },
  'patient.patient_number': { table: 'patients', column: 'patient_number' },
  'patient.blood_group_abo': { table: 'patients', column: 'blood_group_abo' },
  'patient.drug_allergies_notes': { table: 'patients', column: 'drug_allergies_notes' },
  'patient.medical_notes': { table: 'patients', column: 'medical_notes' },
  // Demographics (expanded)
  'patient.title': { table: 'patients', column: 'title' },
  'patient.local_name': { table: 'patients', column: 'local_name' },
  'patient.marital_status': { table: 'patients', column: 'marital_status' },
  'patient.nationality': { table: 'patients', column: 'nationality' },
  'patient.country_of_birth': { table: 'patients', column: 'country_of_birth' },
  'patient.race': { table: 'patients', column: 'race' },
  'patient.religion': { table: 'patients', column: 'religion' },
  'patient.occupation': { table: 'patients', column: 'occupation' },
  'patient.literacy_level': { table: 'patients', column: 'literacy_level' },
  'patient.assigned_doctor': { table: 'patients', column: 'assigned_doctor' },
  'patient.referred_by': { table: 'patients', column: 'referred_by' },
  'patient.location': { table: 'patients', column: 'location' },
  'patient.department_name': { table: 'patients', column: 'department_name' },
  'patient.job_position': { table: 'patients', column: 'job_position' },
  'patient.shift': { table: 'patients', column: 'shift' },
  // Medication / Allergies (expanded)
  'patient.blood_group_rh': { table: 'patients', column: 'blood_group_rh' },
  'patient.food_allergies_notes': { table: 'patients', column: 'food_allergies_notes' },
  'patient.medical_alerts': { table: 'patients', column: 'medical_alerts' },
  'patient.past_medication_notes': { table: 'patients', column: 'past_medication_notes' },
  'patient.current_medication_notes': { table: 'patients', column: 'current_medication_notes' },
  'patient.medical_history': { table: 'patients', column: 'medical_history' },
  // ── patient_contacts table (related-row — upserted alongside patient) ──
  'patient.email': { table: 'patient_contacts', column: 'email' },
  'patient.mobile_number': { table: 'patient_contacts', column: 'mobile_number' },
  'patient.formal_telephone': { table: 'patient_contacts', column: 'formal_telephone' },
  'patient.formal_residential_address': {
    table: 'patient_contacts',
    column: 'formal_residential_address',
  },
  'patient.formal_province_state': { table: 'patient_contacts', column: 'formal_province_state' },
  'patient.formal_district': { table: 'patient_contacts', column: 'formal_district' },
  'patient.formal_postal_code': { table: 'patient_contacts', column: 'formal_postal_code' },
  'patient.mobile_country_code': { table: 'patient_contacts', column: 'mobile_country_code' },
  'patient.formal_unit': { table: 'patient_contacts', column: 'formal_unit' },
  'patient.formal_residential_address_local': {
    table: 'patient_contacts',
    column: 'formal_residential_address_local',
  },
  'patient.formal_sub_district': { table: 'patient_contacts', column: 'formal_sub_district' },
  // Present address
  'patient.present_telephone': { table: 'patient_contacts', column: 'present_telephone' },
  'patient.present_postal_code': { table: 'patient_contacts', column: 'present_postal_code' },
  'patient.present_unit': { table: 'patient_contacts', column: 'present_unit' },
  'patient.present_address': { table: 'patient_contacts', column: 'present_address' },
  'patient.present_address_local': { table: 'patient_contacts', column: 'present_address_local' },
  'patient.present_province_state': { table: 'patient_contacts', column: 'present_province_state' },
  'patient.present_district': { table: 'patient_contacts', column: 'present_district' },
  'patient.present_sub_district': { table: 'patient_contacts', column: 'present_sub_district' },
  // Office
  'patient.office_telephone': { table: 'patient_contacts', column: 'office_telephone' },
  'patient.office_address': { table: 'patient_contacts', column: 'office_address' },
  // ── patient_encounters table ──
  'encounter.type': { table: 'patient_encounters', column: 'type' },
  'encounter.started_at': { table: 'patient_encounters', column: 'started_at' },
  'encounter.provider_name': { table: 'patient_encounters', column: 'provider_name' },
};

/**
 * Multi-row write target map. These binding keys produce fan-out operations:
 * one row per selected value, with soft-delete of existing rows first.
 */
interface MultiRowWriteTarget {
  table: string;
  valueColumn: string;
  staticColumns: Record<string, string>;
  filterColumn: string;
  filterValue: string;
}

const MULTI_ROW_WRITE_TARGET_MAP: Record<string, MultiRowWriteTarget> = {
  'patient.allergies_drug': {
    table: 'patient_allergies',
    valueColumn: 'substance',
    staticColumns: { allergy_type: 'drug' },
    filterColumn: 'allergy_type',
    filterValue: 'drug',
  },
  'patient.allergies_food': {
    table: 'patient_allergies',
    valueColumn: 'substance',
    staticColumns: { allergy_type: 'food' },
    filterColumn: 'allergy_type',
    filterValue: 'food',
  },
};

// ─── Step 1: Extract Candidates ───

/**
 * Extract all eligible `two_way` save candidates from a form schema.
 *
 * A field is eligible when:
 *   - it has V2 binding metadata
 *   - binding.mode === 'two_way'
 *   - it resolves to a registry entry
 *   - the submitted values contain a value for this field
 *
 * Optionally filters by domain groups (same effective group precedence as PRD.148).
 */
export function extractTwoWayCandidates(
  schema: FormFieldsSchemaV1,
  values: Record<string, string>,
  groups?: DomainGroup[],
): CandidateExtractionResult {
  const candidates: TwoWaySaveCandidate[] = [];
  const skipped: SkippedField[] = [];
  const groupSet = groups && groups.length > 0 ? new Set(groups) : null;

  const allFields = flattenFields(schema);

  for (const field of allFields) {
    // Skip unbound fields
    if (!field.binding) continue;

    // Skip non-two_way modes
    if (field.binding.mode !== 'two_way') {
      skipped.push({
        fieldKey: field.key,
        reason: `mode is '${field.binding.mode}', not 'two_way'`,
      });
      continue;
    }

    // Resolve binding key
    const bindingKey = `${field.binding.source}.${field.binding.bindingKey}`;

    // Resolve through registry
    const registryEntry = getSystemFieldByKey(bindingKey);
    if (!registryEntry) {
      skipped.push({
        fieldKey: field.key,
        reason: `bindingKey '${bindingKey}' not found in registry`,
      });
      continue;
    }

    // Check effective domain group filter
    const effectiveGroup = getEffectiveDomainGroup(field);
    if (!effectiveGroup) {
      skipped.push({ fieldKey: field.key, reason: 'no effective domain group' });
      continue;
    }
    if (groupSet && !groupSet.has(effectiveGroup)) {
      skipped.push({
        fieldKey: field.key,
        reason: `group '${effectiveGroup}' not in requested groups`,
      });
      continue;
    }

    // Check submitted value exists
    const value = values[field.key];
    if (value === undefined || value === '') {
      skipped.push({ fieldKey: field.key, reason: 'no submitted value' });
      continue;
    }

    candidates.push({
      fieldKey: field.key,
      bindingKey,
      source: field.binding.source,
      registryEntry,
      domainGroup: effectiveGroup,
      value,
    });
  }

  return { candidates, skipped };
}

// ─── Step 2: Build Domain Write Operations ───

/**
 * Build structured domain write operations from save candidates.
 *
 * Groups single-column candidates by target table (one DomainWriteOp per table).
 * Multi-row candidates (e.g. allergies) produce MultiRowWriteOp entries.
 * Candidates whose bindingKey has no write target mapping are skipped safely.
 */
export function buildDomainWriteOps(candidates: TwoWaySaveCandidate[]): {
  operations: DomainWriteOp[];
  multiRowOps: MultiRowWriteOp[];
  unmapped: SkippedField[];
} {
  const unmapped: SkippedField[] = [];
  const byTable = new Map<string, Record<string, string>>();
  const multiRowOps: MultiRowWriteOp[] = [];

  for (const candidate of candidates) {
    // Check multi-row target first
    const multiTarget = MULTI_ROW_WRITE_TARGET_MAP[candidate.bindingKey];
    if (multiTarget) {
      // Parse the value as a JSON array (multi-select stores ["val1","val2"])
      let values: string[] = [];
      try {
        const parsed = JSON.parse(candidate.value);
        if (Array.isArray(parsed))
          values = parsed.filter((v): v is string => typeof v === 'string' && v.trim() !== '');
      } catch {
        // Not JSON — treat as single value
        if (candidate.value.trim()) values = [candidate.value.trim()];
      }
      if (values.length > 0) {
        multiRowOps.push({
          table: multiTarget.table,
          valueColumn: multiTarget.valueColumn,
          staticColumns: multiTarget.staticColumns,
          filterColumn: multiTarget.filterColumn,
          filterValue: multiTarget.filterValue,
          values,
          description: `MULTI_ROW ${multiTarget.table}: ${values.length} ${multiTarget.filterValue} rows`,
        });
      }
      continue;
    }

    // Single-column target
    const target = WRITE_TARGET_MAP[candidate.bindingKey];
    if (!target) {
      unmapped.push({
        fieldKey: candidate.fieldKey,
        reason: `no write target for '${candidate.bindingKey}'`,
      });
      continue;
    }
    const existing = byTable.get(target.table) ?? {};
    existing[target.column] = candidate.value;
    byTable.set(target.table, existing);
  }

  const operations: DomainWriteOp[] = [];
  for (const [table, columns] of byTable) {
    const colNames = Object.keys(columns);
    operations.push({
      table,
      columns,
      description: `UPDATE ${table}: ${colNames.join(', ')}`,
    });
  }

  return { operations, multiRowOps, unmapped };
}

// ─── Step 3: Orchestrate (Convenience Entry Point) ───

/**
 * Full orchestration: extract candidates → build write operations → return result.
 *
 * This is the primary entry point for form save orchestration. The caller
 * should execute the returned operations via db.execute().
 *
 * @param schema - The form schema (full or domain-group-filtered)
 * @param values - All submitted form field values (field_key → value)
 * @param groups - Optional domain group filter for grouped saves
 */
export function orchestrateTwoWaySave(
  schema: FormFieldsSchemaV1,
  values: Record<string, string>,
  groups?: DomainGroup[],
): OrchestratorResult {
  const { candidates, skipped } = extractTwoWayCandidates(schema, values, groups);
  const { operations, multiRowOps, unmapped } = buildDomainWriteOps(candidates);

  return {
    operations,
    multiRowOps,
    saved: candidates.filter((c) => !unmapped.some((u) => u.fieldKey === c.fieldKey)),
    skipped: [...skipped, ...unmapped],
  };
}

// ─── SQL Builders (for caller convenience) ───

/**
 * Build a parameterized UPDATE SQL statement from a DomainWriteOp.
 * Returns { sql, params } ready for db.execute().
 *
 * The caller must append the WHERE clause with the appropriate ID.
 *
 * @example
 * const { sql, params } = buildUpdateSql(op);
 * await db.execute(sql + ' WHERE id = ?', [...params, patientId]);
 */
export function buildUpdateSql(op: DomainWriteOp): { sql: string; params: string[] } {
  const entries = Object.entries(op.columns);
  const setClauses = entries.map(([col]) => `${col} = ?`);
  setClauses.push("updated_at = datetime('now')");
  const sql = `UPDATE ${op.table} SET ${setClauses.join(', ')}`;
  const params = entries.map(([, val]) => val);
  return { sql, params };
}

/**
 * Build a parameterized INSERT SQL statement from a DomainWriteOp.
 * Merges op.columns with the caller-supplied `baseColumns` (e.g. id, patient_id).
 *
 * @example
 * const { sql, params } = buildInsertSql(op, { id: newId, patient_id: patientId });
 * await db.execute(sql, params);
 */
export function buildInsertSql(
  op: DomainWriteOp,
  baseColumns: Record<string, string>,
): { sql: string; params: string[] } {
  const merged: Record<string, string> = {
    ...baseColumns,
    ...op.columns,
    created_at: "datetime('now')",
    updated_at: "datetime('now')",
  };
  const keys = Object.keys(merged);
  const placeholders: string[] = [];
  const params: string[] = [];
  for (const key of keys) {
    const val = merged[key];
    if (val === "datetime('now')") {
      placeholders.push("datetime('now')");
    } else {
      placeholders.push('?');
      params.push(val);
    }
  }
  const sql = `INSERT INTO ${op.table} (${keys.join(', ')}) VALUES (${placeholders.join(', ')})`;
  return { sql, params };
}
